import { useState } from "react";
import { Lock, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { tr, LANG } from "../lib/i18n";
import { useBranches, branchName } from "../lib/branches";
import { UpgradeDialog } from "./UpgradeDialog";

const EMPTY = { name: "", name_ar: "", city: "", address: "", address_ar: "", phone: "", whatsapp: "", map_url: "", is_main: false, active: true };
const FIELDS = [["name", "br.name"], ["name_ar", "br.name_ar"], ["city", "br.city"], ["address", "br.address"],
  ["address_ar", "br.address_ar"], ["phone", "br.phone"], ["whatsapp", "br.whatsapp"], ["map_url", "br.map_url"]];

function BranchForm({ start, onDone }) {
  const [f, setF] = useState({ ...EMPTY, ...start });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const { id, created_at, ...body } = f;
      if (start?.id) await api.patch(`/branches/${start.id}`, body); else await api.post("/branches", body);
      toast.success(tr("br.saved"));
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="ds-panel p-4 grid sm:grid-cols-2 gap-3" data-testid="branch-form">
      {FIELDS.map(([k, label]) => (
        <label key={k} className="text-xs font-medium">{tr(label)}
          <input className="mt-1 w-full h-10 border border-[var(--divider)] px-3 text-sm" value={f[k] || ""} dir={k.endsWith("_ar") ? "rtl" : undefined}
            onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`branch-field-${k}`} />
        </label>
      ))}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.is_main} onChange={(e) => setF({ ...f, is_main: e.target.checked })} data-testid="branch-field-main" />{tr("br.main")}</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="branch-field-active" />{tr("br.active")}</label>
      <div className="sm:col-span-2 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onDone} data-testid="branch-form-cancel">{tr("x.cancel")}</button>
        <button className="btn-primary" disabled={busy || !f.name.trim()} onClick={save} data-testid="branch-form-save">{tr("x.save")}</button>
      </div>
    </div>
  );
}

function ModeToggle({ data, reload }) {
  const [lock, setLock] = useState(false);
  const set = async (mode) => {
    if (mode === "multiple" && !data.plan_allows) { setLock(true); return; }
    try { await api.put("/branches/mode", { mode }); toast.success(tr("br.mode_saved")); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={tr("br.mode")} data-testid="branch-mode">
      {["one", "multiple"].map((m) => (
        <button key={m} role="radio" aria-checked={data.mode === m} onClick={() => data.mode !== m && set(m)} data-testid={`branch-mode-${m}`}
          className={data.mode === m ? "btn-primary" : "btn-secondary"}>
          {m === "multiple" && !data.plan_allows && <Lock size={14} strokeWidth={1.75} />}{tr(`br.mode_${m /* i18n-keys: one,multiple */}`)}
        </button>
      ))}
      {lock && <UpgradeDialog flag="multi_branch" onClose={() => setLock(false)} />}
    </div>
  );
}

export const BranchesPanel = () => {
  const { data, all, multi, reload } = useBranches();
  const [edit, setEdit] = useState(null);
  if (!data) return null;
  const done = () => { setEdit(null); reload(); };
  return (
    <div className="space-y-5" data-testid="branches-panel">
      <p className="text-sm" style={{ color: "var(--ds-ink-muted)" }}>{tr("br.intro")}</p>
      <ModeToggle data={data} reload={reload} />
      {multi && (
        <>
          {!data.plan_allows && <p className="text-sm" data-testid="branch-readonly-note">{tr("br.read_only_note")}</p>}
          <table className="w-full text-sm" data-testid="branches-table">
            <thead><tr className="text-start"><th className="text-start py-2">{tr("br.name")}</th><th className="text-start">{tr("br.phone")}</th><th className="text-start">{tr("br.status")}</th><th /></tr></thead>
            <tbody>
              {all.map((b) => (
                <tr key={b.id} className="border-t border-[var(--divider)]" data-testid={`branch-row-${b.id}`}>
                  <td className="py-2"><span className="font-medium">{branchName(b, LANG)}</span>{b.is_main && <span className="ms-2 inline-flex items-center gap-1 text-xs"><Star size={12} />{tr("br.main")}</span>}
                    <div className="text-xs" style={{ color: "var(--ds-ink-muted)" }}>{(LANG === "ar" && b.address_ar) || b.address}</div></td>
                  <td className="text-start"><span dir="ltr">{b.phone}</span></td>
                  <td>{b.active ? tr("br.active") : tr("br.inactive")}</td>
                  <td className="text-end"><button className="btn-secondary" onClick={() => setEdit(b)} data-testid={`branch-edit-${b.id}`}>{tr("x.edit")}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {edit ? <BranchForm start={edit} onDone={done} /> : (
            <button className="btn-secondary" onClick={() => (data.plan_allows ? setEdit({}) : null)} disabled={!data.plan_allows} data-testid="branch-add">
              <Plus size={14} strokeWidth={1.75} /> {tr("br.add")}
            </button>
          )}
        </>
      )}
    </div>
  );
};
